export type QueueSearchTimerState = {
  status?: string | null
  queue_id?: string | null
  queued_at?: string | null
  queue_elapsed_seconds?: number
}

export type QueueSearchTimerAnchor = {
  instanceKey: string
  elapsedSeconds: number
  observedAtMonotonicMs: number
  authoritative: boolean
}

export function reconcileQueueSearchTimer(
  previous: QueueSearchTimerAnchor | null,
  state: QueueSearchTimerState | null,
  monotonicNowMs: number,
  wallClockNowMs: number,
): QueueSearchTimerAnchor | null {
  if (state?.status !== 'queued') return null

  const instanceKey = queueSearchInstanceKey(state)
  const observedAtMonotonicMs = finiteNonNegative(monotonicNowMs)
  if (typeof state.queue_elapsed_seconds === 'number') {
    let elapsedSeconds = finiteNonNegative(state.queue_elapsed_seconds)
    if (previous?.authoritative && previous.instanceKey === instanceKey) {
      elapsedSeconds = Math.max(
        elapsedSeconds,
        queueSearchElapsedSeconds(previous, observedAtMonotonicMs),
      )
    }
    return {
      instanceKey,
      elapsedSeconds,
      observedAtMonotonicMs,
      authoritative: true,
    }
  }

  if (previous?.instanceKey === instanceKey) return previous

  const queuedAtMs = parseQueuedAt(state.queued_at)
  if (queuedAtMs !== null) {
    return {
      instanceKey,
      elapsedSeconds: Math.max(0, (finiteNonNegative(wallClockNowMs) - queuedAtMs) / 1000),
      observedAtMonotonicMs,
      authoritative: false,
    }
  }

  return {
    instanceKey,
    elapsedSeconds: 0,
    observedAtMonotonicMs,
    authoritative: false,
  }
}

export function queueSearchElapsedSeconds(
  anchor: QueueSearchTimerAnchor | null,
  monotonicNowMs: number,
): number {
  if (!anchor) return 0
  const elapsedSinceObservation = Math.max(
    0,
    finiteNonNegative(monotonicNowMs) - anchor.observedAtMonotonicMs,
  ) / 1000
  return anchor.elapsedSeconds + elapsedSinceObservation
}

export function formatQueueSearchElapsed(seconds: number): string {
  const wholeSeconds = Math.floor(finiteNonNegative(seconds))
  const minutes = Math.floor(wholeSeconds / 60)
  return `${minutes}:${String(wholeSeconds % 60).padStart(2, '0')}`
}

function queueSearchInstanceKey(state: QueueSearchTimerState): string {
  return `${state.queue_id?.trim() || 'queue'}\u0000${state.queued_at?.trim() || 'pending'}`
}

function parseQueuedAt(value: string | null | undefined): number | null {
  if (!value) return null
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : null
}

function finiteNonNegative(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0
}
